import React from 'react';
import styled from '@emotion/styled';
import { Icon as _Icon } from '@iconify-icon/react';
import _AddIcon from '@iconify-icons/material-symbols/add-circle-outline-rounded';
import _DeleteIcon from '@iconify-icons/material-symbols/delete-outline';
import _CancelIcon from '@iconify-icons/material-symbols/cancel-outline';
import _EditIcon from '@iconify-icons/ic/outline-edit';
import _DragIcon from '@iconify-icons/material-symbols/drag-indicator';
import _RiseIcon from '@iconify-icons/mdi-light/arrow-up';
import _SinkIcon from '@iconify-icons/mdi-light/arrow-down';
import _TopIcon from '@iconify-icons/material-symbols/vertical-align-top';
import _BottomIcon from '@iconify-icons/material-symbols/vertical-align-bottom';
import _DecreaseIcon from '@iconify-icons/ic/outline-remove-circle-outline';
import _VertBarIcon from '@iconify-icons/ic/outline-view-column';
import _HoriBarIcon from '@iconify-icons/ic/outline-table-rows';
import _QuestionIcon from '@iconify-icons/ic/outline-help-outline';
import _LightIcon from '@iconify-icons/ic/outline-lightbulb';
import MdiDotsVertical from '@iconify-icons/mdi/dots-vertical';
import PushPin from '@iconify-icons/material-symbols/push-pin-outline';
import MdiPinOffOutline from '@iconify-icons/mdi/pin-off-outline';
import { classnames } from './Utils';

/**
 * @type {React.FC<import('@iconify-icon/react').IconifyIconProps & {disable?: boolean, className?: string, theme?: 'normal'}>}
 */
export const Icon = ({ className, disable, theme = 'primary', ...props }) => {
	const iconClassName = classnames('H2-IconButton', {
		'disable': disable,
		[className]: className,
		[theme]: theme,
	});
	return <_Icon className={iconClassName} {...props} />;
};

export const RemoveBtn = ({ ...props }) => {
	return <Icon className='H2-IconButton' icon={_DeleteIcon} {...props} />;
};

export const AddBtn = ({ ...props }) => {
	return <Icon className='H2-IconButton' icon={_AddIcon} {...props}/>;
};

export function EditBtn(props) {
	return <Icon className="H2-IconButton" icon={_EditIcon} {...props} />;
}

export function DragBtn(props) {
	return <Icon className="H2-IconButton" icon={_DragIcon} {...props} />;
}

export function CancelBtn(props) {
	return <Icon className="H2-IconButton" icon={_CancelIcon} {...props} />;
}

export function RiseBtn(props) {
	return <Icon className="H2-IconButton" icon={_RiseIcon} {...props} />;
}

export function SinkBtn(props) {
	return <Icon className="H2-IconButton" icon={_SinkIcon} {...props} />;
}

export function TopBtn(props) {
	return <Icon className="H2-IconButton" icon={_TopIcon} {...props} />;
}

export function BottomBtn(props) {
	return <Icon className="H2-IconButton" icon={_BottomIcon} {...props} />;
}

export function MinusBtn(props) {
	return <Icon className="H2-IconButton" icon={_DecreaseIcon} {...props} />;
}

export function ColumnBtn(props) {
	return <Icon className="H2-IconButton" icon={_VertBarIcon} {...props} />;
}

export function RowBtn(props) {
	return <Icon className="H2-IconButton" icon={_HoriBarIcon} {...props} />;
}

export function AskBtn(props) {
	return <Icon className="H2-IconButton" icon={_QuestionIcon} {...props} />;
}

export function IdeaBtn(props) {
	return <Icon className="H2-IconButton" icon={_LightIcon} {...props} />;
}

export function DotBtn(props) {
	return <Icon className="H2-IconButton" icon={MdiDotsVertical} {...props} />;
}

export function PinBtn(props) {
	return <Icon className="H2-IconButton" icon={PushPin} {...props} />;
}

export function UnpinBtn(props) {
	return <Icon className="H2-IconButton" icon={MdiPinOffOutline} {...props} />;
}

// AI 按鈕的 styled components
const AIBtnContainer = styled.button`
	display: inline-flex;
	align-items: stretch;
	justify-content: center;
	padding: 0;
	border: 2px solid;
	border-radius: 4px;
	background-color: #FFFFFF;
	cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
	transition: background-color 0.2s ease, border-color 0.2s ease;
	position: relative;
	overflow: hidden;
	border: none;

	/* 正常狀態的邊框 - 使用雙層邊框實現漸層 + 陰影效果 */
	&::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		border-radius: 4px;
		padding: 2px;
		background: ${props => props.disabled
			? '#A9A9A9'
			: 'linear-gradient(126deg, rgba(15, 122, 245, 1) 0%, rgba(186, 41, 255, 1) 21%, rgba(82, 74, 237, 1) 72%, rgba(15, 122, 245, 1) 97%)'
		};
		mask:
			linear-gradient(#fff 0 0) content-box,
			linear-gradient(#fff 0 0);
		mask-composite: exclude;
		pointer-events: none;
	}

	&::after {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		border-radius: 4px;
		box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.2) inset;
		pointer-events: none;
		opacity: ${props => props.disabled ? 0 : 0.3};
	}

	/* Hover 狀態 */
	&:not(:disabled):hover {
		background-color: #FBF2FF;
	}
`;

const AIBtnContent = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	gap: 2px;
	padding: 7px 16px;
	z-index: 1;
	background-color: none;
	border: none;
`;

const AIBtnIconWrapper = styled.div`
	width: 14px;
	height: 14px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;

	${props => props.disabled && `
		opacity: 0.5;
		filter: grayscale(1);
	`}
`;

const AIBtnText = styled.span`
	font-weight: 700;
	font-size: 14px;
	line-height: 1.25em;
	text-align: center;
	white-space: nowrap;

	/* 正常狀態的文字顏色 - 使用漸層 */
	${props => !props.disabled && `
		background: linear-gradient(126deg,
			rgba(15, 122, 245, 1) 0%,
			rgba(186, 41, 255, 1) 21%,
			rgba(82, 74, 237, 1) 72%,
			rgba(15, 122, 245, 1) 97%
		);
		background-clip: text;
		text-fill-color: transparent;
	`}

	/* 禁用狀態的文字顏色 */
	${props => props.disabled && `
		background: #A9A9A9;
		background-clip: text;
		text-fill-color: transparent;
	`}
`;

export const AIIcon = () => {
	return (
		<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
			<path fillRule="evenodd" clipRule="evenodd" d="M3.17062 6.30198L4.2275 2.95024H5.59453L6.6514 6.30198L9.82203 7.41923V8.86435L6.6514 9.98159L5.59453 13.3333H4.2275L3.17062 9.9816L0 8.86435V7.41923L3.17062 6.30198ZM4.91101 5.59963L4.42374 7.14497L3.96806 7.62667L2.50622 8.14179L3.96806 8.6569L4.42374 9.13861L4.91101 10.6839L5.39829 9.13861L5.85397 8.6569L7.3158 8.14179L5.85397 7.62667L5.39829 7.14497L4.91101 5.59963Z" fill="url(#paint0_linear_5270_1599)"/>
			<path fillRule="evenodd" clipRule="evenodd" d="M3.17062 6.30198L4.2275 2.95024H5.59453L6.6514 6.30198L9.82203 7.41923V8.86435L6.6514 9.98159L5.59453 13.3333H4.2275L3.17062 9.9816L0 8.86435V7.41923L3.17062 6.30198ZM4.91101 5.59963L4.42374 7.14497L3.96806 7.62667L2.50622 8.14179L3.96806 8.6569L4.42374 9.13861L4.91101 10.6839L5.39829 9.13861L5.85397 8.6569L7.3158 8.14179L5.85397 7.62667L5.39829 7.14497L4.91101 5.59963Z" fill="black" fillOpacity="0.2"/>
			<path fillRule="evenodd" clipRule="evenodd" d="M9.70274 1.78255L10.2648 0H11.085L11.6471 1.78255L13.3333 2.37673V3.24381L11.6471 3.83799L11.085 5.62054H10.2648L9.70274 3.83799L8.01651 3.24381V2.37673L9.70274 1.78255Z" fill="url(#paint1_linear_5270_1599)"/>
			<path fillRule="evenodd" clipRule="evenodd" d="M9.70274 1.78255L10.2648 0H11.085L11.6471 1.78255L13.3333 2.37673V3.24381L11.6471 3.83799L11.085 5.62054H10.2648L9.70274 3.83799L8.01651 3.24381V2.37673L9.70274 1.78255Z" fill="black" fillOpacity="0.2"/>
			<defs>
				<linearGradient id="paint0_linear_5270_1599" x1="-0.941122" y1="1.23457" x2="13.7138" y2="2.44886" gradientUnits="userSpaceOnUse">
					<stop offset="0.00480769" stopColor="#0F7AF5"/>
					<stop offset="0.264423" stopColor="#BA29FF"/>
					<stop offset="0.754808" stopColor="#524AED"/>
					<stop offset="1" stopColor="#0F7AF5"/>
				</linearGradient>
				<linearGradient id="paint1_linear_5270_1599" x1="-0.941122" y1="1.23457" x2="13.7138" y2="2.44886" gradientUnits="userSpaceOnUse">
					<stop offset="0.00480769" stopColor="#0F7AF5"/>
					<stop offset="0.264423" stopColor="#BA29FF"/>
					<stop offset="0.754808" stopColor="#524AED"/>
					<stop offset="1" stopColor="#0F7AF5"/>
				</linearGradient>
			</defs>
		</svg>
	);
};

/**
 * AI 按鈕組件
 * @param {Object} props
 * @param {boolean} props.disable - 是否禁用
 * @param {Function} props.onClick - 點擊事件處理函數
 * @param {string} props.children - 按鈕文字，預設為 "AI 幫你排班"
 * @param {number|string} props.height - 按鈕高度，可以是數字（像素）或字符串（帶單位）
 * @param {string} props.className - 額外的 CSS class
 */
export const AIBtn = ({
	disable = false,
	onClick,
	children = OptionUtils.getLabel('ai-shift.m3.m310410_new', 'aIHelpsYouScheduleShifts'),
	className = '',
	height,
	...props
}) => {
	return (
		<AIBtnContainer
			className={`ai-btn${className ? ` ${className}` : ''}`}
			onClick={disable ? undefined : onClick}
			disabled={disable}
			{...props}
			style={{
				height,
				...props.style,
			}}
		>
			<AIBtnContent>
				<AIBtnIconWrapper disabled={disable}>
					<AIIcon />
				</AIBtnIconWrapper>
				<AIBtnText disabled={disable}>{children}</AIBtnText>
			</AIBtnContent>
		</AIBtnContainer>
	);
};
